import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
// fork(oem): the rail answers from the OEM+ local engine and can move the
// main pane, so the provider needs the router, the persona and the engine.
import { useLocation, useNavigate } from 'react-router-dom';
import type { ImperativePanelHandle } from 'react-resizable-panels';
import {
  LOCAL_ENGINE_MODEL_ID,
  NEW_THREAD_ID,
  TAIL_PRESERVE_MESSAGE_COUNT,
  type ChatMessage,
  type ChatMessagePart,
  type ChatThread,
  type ComposerStyleId,
  type PermissionMode,
} from './chat-types';
import { SEED_THREADS } from './thread-seed';
// fork(oem):
// fork(oem): `askOem()` dispatches this; see shared/assistant/ask.ts.
import { OEM_ASK_EVENT } from './assistant/ask';
import {
  localAnswer,
  type ConversationMemory,
  type OemAnswer,
} from './assistant/local-answer';
// fork(oem v1.8): the 06:05 briefing and the persona-switch line.
import {
  BRIEFING_TIME,
  morningBriefing,
  personaSwitchMessage,
  type OpeningMessage,
} from './assistant/briefing';
import { useBrand } from './BrandContext';
import { OEM_NOW_MS, OEM_NOW_ISO } from './oem-clock';
import { orgLabel, useOemPersona } from './OemPersonaContext';

export const AI_RAIL_OPEN_KEY = 'mock-ui-ai-rail-open';

/** fork(oem): the pause before a reply, kept from the foundation's mock. */
const THINKING_MS = 800;

let nextMessageId = 1000;

/**
 * fork(oem): message timestamps come from the frozen OEM clock, never the wall
 * clock — the dataset's "now" is 09 Sep 2026 06:00 AEST and the rail is a
 * snapshot target. Sliced off the ISO literal so the result does not depend on
 * the host timezone.
 */
const OEM_TIME = OEM_NOW_ISO.slice(11, 16);

function nowHHMM(): string {
  return OEM_TIME;
}

/**
 * fork(oem): a monotonic stand-in for `Date.now()` on `updatedAt`, so thread
 * ordering still advances without reading the wall clock.
 */
let updatedAtCursor = OEM_NOW_MS;
const nextUpdatedAt = (): number => (updatedAtCursor += 1000);

/** fork(oem v1.8): the thinking line before the first tool reports in. */
const THINKING_LABEL = 'Thinking…';

/** fork(oem v1.8): the demo's `logTool()` label — "Working… a → b". */
const workingLabel = (trace: readonly string[]): string =>
  trace.length ? `Working… ${trace.join(' → ')}` : THINKING_LABEL;

/** fork(oem v1.8): the briefing's fixed message id — below every live id. */
const BRIEFING_MESSAGE_ID = 1;

/** fork(oem v1.8): an opening message (briefing, persona line) as a chat message. */
function openingToMessage(id: number, time: string, opening: OpeningMessage): ChatMessage {
  const parts: ChatMessagePart[] = [{ type: 'markdown', content: opening.markdown }];
  if (opening.actions?.length) {
    parts.push({ type: 'actions', actions: opening.actions.map((a) => ({ ...a })) });
  }
  return {
    id,
    who: 'bot',
    time,
    body: opening.markdown,
    citations: opening.sources.map((label) => ({ label })),
    parts,
  };
}

/** fork(oem): what the rail shows when the local engine or its data fails. */
function failureAnswer(error: unknown): OemAnswer {
  const detail = error instanceof Error ? error.message : String(error);
  return {
    markdown: `I could not answer that one. ${detail}`,
    sources: [],
    tools: ['(local engine)'],
  };
}

function readRailOpen(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(AI_RAIL_OPEN_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeRailOpen(open: boolean) {
  try {
    window.localStorage.setItem(AI_RAIL_OPEN_KEY, open ? 'true' : 'false');
  } catch {
    // Storage may be unavailable.
  }
}

function cloneThreads(): ChatThread[] {
  return SEED_THREADS.map((t) => ({
    ...t,
    messages: t.messages.map((m) => ({ ...m, citations: m.citations?.map((c) => ({ ...c })) })),
  }));
}

type ChatContextValue = {
  threads: ChatThread[];
  currentThreadId: string;
  messages: ChatMessage[];
  /** Messages hidden by manual compact — older turns replaced by a notice row. */
  compactedEarlierCount: number;
  thinking: boolean;
  /**
   * fork(oem v1.8): what the thinking line says — "Thinking…", then
   * "Working… a → b" as the local engine reports each tool it runs.
   */
  thinkingLabel: string;
  /**
   * fork(oem v1.8): the 06:05 morning briefing. The welcome state shows it
   * above the composer; the first message sent from a new chat keeps it as
   * the conversation's opening turn.
   */
  briefing: ChatMessage;
  draft: string;
  setDraft: (text: string) => void;
  editingMessageId: number | null;
  setEditingMessageId: (id: number | null) => void;
  editDraft: string;
  setEditDraft: (text: string) => void;
  modelId: string;
  setModelId: (id: string) => void;
  permissionMode: PermissionMode;
  setPermissionMode: (mode: PermissionMode) => void;
  composerStyle: ComposerStyleId;
  setComposerStyle: (style: ComposerStyleId) => void;
  railOpen: boolean;
  openRail: () => void;
  closeRail: () => void;
  railPanelRef: RefObject<ImperativePanelHandle | null>;
  send: (text: string) => void;
  resend: (text: string) => void;
  compactConversation: () => void;
  newChat: (projectSlug?: string) => void;
  selectThread: (id: string) => void;
  renameThread: (title: string) => void;
  deleteCurrentThread: () => void;
  deleteThread: (id: string) => void;
  toggleThreadPin: (id: string) => void;
  moveThreadToProject: (id: string, slug: string) => void;
  moveThreadOut: (id: string) => void;
  currentTitle: string;
  /** When the active thread is project-scoped, switch to the latest workspace thread or new chat. */
  ensureWorkspaceThread: () => void;
};

const ChatContext = createContext<ChatContextValue | null>(null);

export function ChatProvider({ children }: { children: ReactNode }) {
  // fork(oem): the engine needs the route and the persona the question was
  // asked from; both are read through refs so `send` keeps a stable identity.
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { personaId, persona, switchSeq } = useOemPersona();
  const { brand } = useBrand();
  const pathnameRef = useRef(pathname);
  const personaRef = useRef(personaId);
  pathnameRef.current = pathname;
  personaRef.current = personaId;
  /** Guards against a stale reply landing after a thread switch. */
  const replyTokenRef = useRef(0);
  /**
   * fork(oem v1.8): the engine's conversation memory, per thread (the welcome
   * thread included), so "why?" or "and at Kalbarri?" resolves against the
   * previous turn of *this* thread. A ref: memory never renders.
   */
  const memoryRef = useRef(new Map<string, ConversationMemory>());
  /** fork(oem v1.8): computed once from the seeds — the data is frozen. */
  const [briefing] = useState<ChatMessage>(() =>
    openingToMessage(BRIEFING_MESSAGE_ID, BRIEFING_TIME, morningBriefing()),
  );
  const [thinkingLabel, setThinkingLabel] = useState(THINKING_LABEL);

  const [threads, setThreads] = useState<ChatThread[]>(cloneThreads);
  const [currentThreadId, setCurrentThreadId] = useState<string>(NEW_THREAD_ID);
  const [welcomeMessages, setWelcomeMessages] = useState<ChatMessage[]>([]);
  const [welcomeTitle, setWelcomeTitle] = useState('New chat');
  /** Project scope for an ephemeral new chat (`NEW_THREAD_ID`); cleared on workspace new chat. */
  const [newChatProjectSlug, setNewChatProjectSlug] = useState<string | null>(null);
  const [thinking, setThinking] = useState(false);
  const [draft, setDraft] = useState('');
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState('');
  // fork(oem v1.8): the only engine that answers — see `CHAT_MODELS`.
  const [modelId, setModelId] = useState(LOCAL_ENGINE_MODEL_ID);
  const [permissionMode, setPermissionMode] = useState<PermissionMode>('default');
  const [composerStyle, setComposerStyle] = useState<ComposerStyleId>('default');
  const [compactedByThread, setCompactedByThread] = useState<Record<string, number>>({});
  const [railOpen, setRailOpenState] = useState<boolean>(readRailOpen);
  const railPanelRef = useRef<ImperativePanelHandle | null>(null);
  const thinkingTimerRef = useRef<number | null>(null);

  const setRailOpen = useCallback((open: boolean) => {
    setRailOpenState(open);
    writeRailOpen(open);
    const handle = railPanelRef.current;
    if (!handle) return;
    if (open) {
      if (handle.isCollapsed()) handle.expand(25);
    } else if (!handle.isCollapsed()) {
      handle.collapse();
    }
  }, []);

  const openRail = useCallback(() => setRailOpen(true), [setRailOpen]);
  const closeRail = useCallback(() => setRailOpen(false), [setRailOpen]);

  const currentThread = useMemo(
    () => threads.find((t) => t.id === currentThreadId),
    [threads, currentThreadId],
  );

  const messages = currentThreadId === NEW_THREAD_ID ? welcomeMessages : (currentThread?.messages ?? []);

  const compactedEarlierCount = Math.min(
    compactedByThread[currentThreadId] ?? 0,
    Math.max(0, messages.length - TAIL_PRESERVE_MESSAGE_COUNT),
  );

  const currentTitle =
    currentThreadId === NEW_THREAD_ID ? welcomeTitle : (currentThread?.title ?? 'New chat');

  /**
   * fork(oem): the reply comes from the OEM+ local engine
   * (`shared/assistant/local-answer.ts`) instead of the foundation's one
   * scripted line. The 800 ms thinking pause is kept as a *floor*, not a
   * timeout: the answer and the pause are awaited together, so a cold data
   * fetch never shortens the beat and a warm one never lengthens it.
   */
  const appendBotReply = useCallback(
    (threadId: string, isWelcome: boolean, prompt: string) => {
      if (thinkingTimerRef.current) window.clearTimeout(thinkingTimerRef.current);
      setThinking(true);
      setThinkingLabel(THINKING_LABEL);
      const token = ++replyTokenRef.current;

      // fork(oem v1.8): hand the engine this thread's memory and listen for
      // its tool trace, so the thinking line reads "Working… a → b".
      const answering = localAnswer(prompt, {
        personaId: personaRef.current,
        pathname: pathnameRef.current,
        memory: memoryRef.current.get(threadId),
        onProgress: (trace) => {
          if (replyTokenRef.current === token) setThinkingLabel(workingLabel(trace));
        },
      }).catch(failureAnswer);

      const pause = new Promise<void>((resolve) => {
        thinkingTimerRef.current = window.setTimeout(resolve, THINKING_MS);
      });

      void Promise.all([answering, pause]).then(([answer]) => {
        if (replyTokenRef.current !== token) return;
        if (answer.memory) memoryRef.current.set(threadId, answer.memory);

        const id = nextMessageId++;
        const parts: ChatMessagePart[] = [];
        if (answer.tools.length) {
          parts.push({ type: 'tool', verb: 'Ran', preview: answer.tools.join(' → ') });
        }
        parts.push({ type: 'markdown', content: answer.markdown });
        if (answer.artifact) {
          parts.push({
            type: 'notice',
            title: answer.artifact.title,
            kind: answer.artifact.kind,
            subtitle: answer.artifact.subtitle,
            note: answer.artifact.note,
          });
        }
        if (answer.report) {
          parts.push({
            type: 'report',
            runId: id,
            ...answer.report,
            steps: answer.report.steps.map((step) => ({ ...step })),
          });
        }
        if (answer.actions?.length) {
          parts.push({ type: 'actions', actions: answer.actions.map((a) => ({ ...a })) });
        }

        const reply: ChatMessage = {
          id,
          who: 'bot',
          time: nowHHMM(),
          body: answer.markdown,
          citations: answer.sources.map((label) => ({ label })),
          parts,
        };

        setThinking(false);
        if (isWelcome) {
          setWelcomeMessages((prev) => [...prev, reply]);
        } else {
          setThreads((prev) =>
            prev.map((t) =>
              t.id === threadId
                ? { ...t, messages: [...t.messages, reply], updatedAt: nextUpdatedAt() }
                : t,
            ),
          );
        }
        if (answer.navigate) navigate(answer.navigate);
      });
    },
    [navigate],
  );

  const send = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      const userMsg: ChatMessage = {
        id: nextMessageId++,
        who: 'user',
        time: nowHHMM(),
        body: trimmed,
      };
      setDraft('');
      setEditingMessageId(null);
      if (currentThreadId === NEW_THREAD_ID) {
        setWelcomeMessages((prev) => {
          if (prev.length === 0 && welcomeTitle === 'New chat') {
            setWelcomeTitle(trimmed.slice(0, 48));
          }
          // fork(oem v1.8): the briefing the welcome state showed stays as the
          // conversation's opening turn.
          return prev.length === 0 ? [briefing, userMsg] : [...prev, userMsg];
        });
        appendBotReply(NEW_THREAD_ID, true, trimmed);
      } else {
        setThreads((prev) =>
          prev.map((t) =>
            t.id === currentThreadId
              ? {
                  ...t,
                  messages: [...t.messages, userMsg],
                  updatedAt: nextUpdatedAt(),
                  title: t.title === 'New chat' ? trimmed.slice(0, 48) : t.title,
                }
              : t,
          ),
        );
        appendBotReply(currentThreadId, false, trimmed);
      }
    },
    [appendBotReply, briefing, currentThreadId, welcomeTitle],
  );

  const resend = useCallback(
    (text: string) => {
      send(text);
    },
    [send],
  );

  /**
   * fork(oem): `window.dispatchEvent(new CustomEvent('oem:ask', { detail }))`
   * from any scene opens the rail and asks the question. P3a's Today panel and
   * `chrome-oem/AskStrip.tsx` are the callers; nothing imports `send` across
   * the scene boundary to do it.
   */
  useEffect(() => {
    const onAsk = (event: Event) => {
      const prompt = (event as CustomEvent<string>).detail;
      if (typeof prompt !== 'string' || !prompt.trim()) return;
      setRailOpen(true);
      send(prompt);
    };
    window.addEventListener(OEM_ASK_EVENT, onAsk);
    return () => window.removeEventListener(OEM_ASK_EVENT, onAsk);
  }, [send, setRailOpen]);

  /**
   * fork(oem v1.8): demo line 4100. A "VIEWING AS" switch posts "Now viewing
   * as **X** (org). I'll weight answers to …" into the open thread, with one
   * button for that persona's first suggested question. Keyed on `switchSeq`,
   * which only moves on a user's switch — never on first paint or on the
   * stored persona being rehydrated.
   */
  const currentThreadRef = useRef(currentThreadId);
  currentThreadRef.current = currentThreadId;
  const announcedSwitchRef = useRef(switchSeq);
  useEffect(() => {
    if (switchSeq === announcedSwitchRef.current) return;
    announcedSwitchRef.current = switchSeq;
    const message = openingToMessage(
      nextMessageId++,
      nowHHMM(),
      personaSwitchMessage(persona, orgLabel(persona, brand.lockup)),
    );
    const threadId = currentThreadRef.current;
    if (threadId === NEW_THREAD_ID) {
      setWelcomeMessages((prev) => (prev.length === 0 ? [briefing, message] : [...prev, message]));
    } else {
      setThreads((prev) =>
        prev.map((t) =>
          t.id === threadId
            ? { ...t, messages: [...t.messages, message], updatedAt: nextUpdatedAt() }
            : t,
        ),
      );
    }
  }, [switchSeq, persona, brand.lockup, briefing]);

  const compactConversation = useCallback(() => {
    const total = messages.length;
    if (total <= TAIL_PRESERVE_MESSAGE_COUNT) return;
    const hidden = total - TAIL_PRESERVE_MESSAGE_COUNT;
    setCompactedByThread((prev) => ({
      ...prev,
      [currentThreadId]: hidden,
    }));
  }, [currentThreadId, messages.length]);

  const newChat = useCallback((projectSlug?: string) => {
    setCurrentThreadId(NEW_THREAD_ID);
    setWelcomeMessages([]);
    // fork(oem v1.8): a new chat starts with no conversation memory.
    memoryRef.current.delete(NEW_THREAD_ID);
    setWelcomeTitle('New chat');
    setNewChatProjectSlug(projectSlug ?? null);
    setDraft('');
    setEditingMessageId(null);
    setThinking(false);
    if (thinkingTimerRef.current) window.clearTimeout(thinkingTimerRef.current);
    setCompactedByThread((prev) => {
      if (!(NEW_THREAD_ID in prev)) return prev;
      const next = { ...prev };
      delete next[NEW_THREAD_ID];
      return next;
    });
  }, []);

  const selectThread = useCallback((id: string) => {
    setCurrentThreadId(id);
    setNewChatProjectSlug(null);
    setDraft('');
    setEditingMessageId(null);
    setThinking(false);
    if (thinkingTimerRef.current) window.clearTimeout(thinkingTimerRef.current);
  }, []);

  const ensureWorkspaceThread = useCallback(() => {
    const isProjectScopedNewChat =
      currentThreadId === NEW_THREAD_ID && newChatProjectSlug !== null;
    const active = threads.find((t) => t.id === currentThreadId);
    const isProjectScopedThread = Boolean(active?.projectSlug);
    if (!isProjectScopedNewChat && !isProjectScopedThread) return;

    const workspaceThreads = threads.filter((t) => !t.projectSlug);
    if (workspaceThreads.length === 0) {
      newChat();
      return;
    }
    const latest = workspaceThreads.reduce((a, b) => (b.updatedAt > a.updatedAt ? b : a));
    selectThread(latest.id);
  }, [currentThreadId, newChatProjectSlug, threads, newChat, selectThread]);

  const renameThread = useCallback(
    (title: string) => {
      const trimmed = title.trim();
      if (!trimmed) return;
      if (currentThreadId === NEW_THREAD_ID) {
        setWelcomeTitle(trimmed);
        return;
      }
      setThreads((prev) =>
        prev.map((t) => (t.id === currentThreadId ? { ...t, title: trimmed } : t)),
      );
    },
    [currentThreadId],
  );

  const deleteCurrentThread = useCallback(() => {
    newChat();
  }, [newChat]);

  const deleteThread = useCallback(
    (id: string) => {
      const deleted = threads.find((t) => t.id === id);
      setThreads((prev) => prev.filter((t) => t.id !== id));
      memoryRef.current.delete(id); // fork(oem v1.8)
      if (currentThreadId === id) {
        newChat(deleted?.projectSlug);
      }
    },
    [currentThreadId, threads, newChat],
  );

  const toggleThreadPin = useCallback((id: string) => {
    setThreads((prev) =>
      prev.map((t) => (t.id === id ? { ...t, pinned: !t.pinned } : t)),
    );
  }, []);

  const moveThreadToProject = useCallback((id: string, slug: string) => {
    setThreads((prev) =>
      prev.map((t) => (t.id === id ? { ...t, projectSlug: slug } : t)),
    );
  }, []);

  const moveThreadOut = useCallback((id: string) => {
    setThreads((prev) =>
      prev.map((t) => (t.id === id ? { ...t, projectSlug: undefined } : t)),
    );
  }, []);

  const value = useMemo<ChatContextValue>(
    () => ({
      threads,
      currentThreadId,
      messages,
      compactedEarlierCount,
      thinking,
      thinkingLabel,
      briefing,
      draft,
      setDraft,
      editingMessageId,
      setEditingMessageId,
      editDraft,
      setEditDraft,
      modelId,
      setModelId,
      permissionMode,
      setPermissionMode,
      composerStyle,
      setComposerStyle,
      railOpen,
      openRail,
      closeRail,
      railPanelRef,
      send,
      resend,
      compactConversation,
      newChat,
      selectThread,
      renameThread,
      deleteCurrentThread,
      deleteThread,
      toggleThreadPin,
      moveThreadToProject,
      moveThreadOut,
      currentTitle,
      ensureWorkspaceThread,
    }),
    [
      threads,
      currentThreadId,
      messages,
      compactedEarlierCount,
      thinking,
      thinkingLabel,
      briefing,
      draft,
      editingMessageId,
      editDraft,
      modelId,
      permissionMode,
      composerStyle,
      railOpen,
      openRail,
      closeRail,
      send,
      resend,
      compactConversation,
      newChat,
      selectThread,
      renameThread,
      deleteCurrentThread,
      deleteThread,
      toggleThreadPin,
      moveThreadToProject,
      moveThreadOut,
      currentTitle,
      ensureWorkspaceThread,
    ],
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat(): ChatContextValue {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used inside <ChatProvider>');
  return ctx;
}
